// client/src/components/Navigation.tsx - RESPONSIVE WITH MOBILE MENU
import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../supabase';
import Logo from './Logo';

export default function Navigation() {
    const [user, setUser] = useState<any>(null);
    const [userType, setUserType] = useState<'attorney' | 'expert' | null>(null);
    const [hasProfile, setHasProfile] = useState(false);
    const [profileVerified, setProfileVerified] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [mobileOpen, setMobileOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        checkUser();
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            if (session?.user) {
                setUser(session.user);
                checkUserProfile(session.user.id);
                checkAdminStatus();
            } else {
                setUser(null);
                setUserType(null);
                setHasProfile(false);
                setProfileVerified(false);
                setIsAdmin(false);
            }
            setLoading(false);
        });
        return () => subscription.unsubscribe();
    }, []);

    useEffect(() => {
        if (user) checkUserProfile(user.id);
    }, [location.pathname]);

    // Close mobile menu on route change
    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    const checkUser = async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
                setUser(user);
                await checkUserProfile(user.id);
                await checkAdminStatus();
            }
        } catch (error) {
            console.error('Error checking user:', error);
        } finally {
            setLoading(false);
        }
    };

    const checkAdminStatus = async () => {
        try {
            const { data: adminResult } = await supabase.rpc('check_is_admin');
            setIsAdmin(!!adminResult);
        } catch {
            setIsAdmin(false);
        }
    };

    const checkUserProfile = async (userId: string) => {
        try {
            const { data: expert } = await supabase
                .from('experts')
                .select('id, is_verified')
                .eq('user_id', userId)
                .maybeSingle();

            if (expert) {
                setUserType('expert');
                setHasProfile(true);
                setProfileVerified(expert.is_verified);
                return;
            }

            const { data: attorney } = await supabase
                .from('attorneys')
                .select('id, is_verified')
                .eq('user_id', userId)
                .maybeSingle();

            if (attorney) {
                setUserType('attorney');
                setHasProfile(true);
                setProfileVerified(attorney.is_verified);
                return;
            }

            const userMetadata = user?.user_metadata;
            if (userMetadata?.user_type === 'expert') {
                setUserType('expert');
                setHasProfile(false);
            } else if (userMetadata?.user_type === 'attorney') {
                setUserType('attorney');
                setHasProfile(false);
            }
        } catch (error) {
            console.error('Error checking user profile:', error);
        }
    };

    const handleSignOut = async () => {
        await supabase.auth.signOut();
        navigate('/');
    };

    const isActive = (path: string) => location.pathname === path;

    if (loading) {
        return (
            <nav className="bg-white/95 backdrop-blur-sm shadow-soft border-b border-slate-200 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between h-16">
                        <div className="flex items-center">
                            <Logo />
                            <span className="ml-3 text-xl font-serif font-bold text-primary-900">Expert Link Pro</span>
                        </div>
                    </div>
                </div>
            </nav>
        );
    }

    return (
        <nav className="bg-white/95 backdrop-blur-sm shadow-soft border-b border-slate-200 sticky top-0 z-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between h-16">
                    {/* Logo */}
                    <div className="flex items-center">
                        <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
                            <Logo />
                            <span className="ml-3 text-xl font-serif font-bold text-primary-900 hidden sm:inline">Expert Link Pro</span>
                        </Link>
                    </div>

                    {/* Desktop Nav */}
                    <div className="hidden lg:flex items-center space-x-4">
                        {user ? (
                            <>
                                {isAdmin && (
                                    <>
                                        <div className="px-3 py-2 bg-gradient-to-r from-primary-800 to-primary-900 text-white rounded-lg text-sm font-medium flex items-center space-x-2">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                            </svg>
                                            <span>Administrator</span>
                                        </div>
                                        <Link to="/admin-verification" className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 bg-primary-800 text-white hover:bg-primary-900 ${isActive('/admin-verification') ? 'ring-2 ring-primary-300' : ''}`}>
                                            Admin Panel
                                        </Link>
                                    </>
                                )}

                                {!isAdmin && (
                                    <>
                                        {!hasProfile && userType && (
                                            <Link to={userType === 'expert' ? '/expert-intake' : '/attorney-intake'} className="px-4 py-2 bg-gradient-to-r from-secondary-600 to-secondary-700 text-white rounded-lg hover:from-secondary-700 hover:to-secondary-800 transition-all duration-200 font-medium text-sm">
                                                Complete Registration
                                            </Link>
                                        )}
                                        {hasProfile && !profileVerified && (
                                            <div className="px-3 py-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg text-sm font-medium">
                                                Pending Verification
                                            </div>
                                        )}
                                        {hasProfile && (
                                            <Link to={userType === 'attorney' ? '/attorney-dashboard' : '/expert-dashboard'} className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${isActive(userType === 'attorney' ? '/attorney-dashboard' : '/expert-dashboard') ? 'bg-primary-100 text-primary-800' : 'text-slate-700 hover:bg-slate-50'}`}>
                                                Dashboard
                                            </Link>
                                        )}
                                    </>
                                )}

                                {hasProfile && (
                                    <Link to="/my-profile" className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${isActive('/my-profile') ? 'bg-primary-100 text-primary-800' : 'text-slate-700 hover:bg-slate-50'}`}>
                                        Profile
                                    </Link>
                                )}

                                <div className="flex items-center space-x-3">
                                    <div className="h-8 w-px bg-slate-300"></div>
                                    <div className="text-sm text-slate-600">
                                        {user.email}
                                        {isAdmin && <span className="block text-xs text-primary-600 font-medium">ADMINISTRATOR</span>}
                                    </div>
                                    <button onClick={handleSignOut} className="px-3 py-2 rounded-lg text-sm font-medium text-red-700 hover:bg-red-50 transition-all duration-200">
                                        Sign Out
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <Link to="/how-it-works" className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50">How It Works</Link>
                                <Link to="/pricing" className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50">Pricing</Link>
                                <Link to="/login" className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50">Sign In</Link>
                                <Link to="/signup" className="px-4 py-2 rounded-lg text-sm font-medium bg-gradient-to-r from-primary-800 to-primary-900 text-white hover:from-primary-900 hover:to-primary-950 shadow-md">Get Started</Link>
                            </>
                        )}
                    </div>

                    {/* Mobile hamburger */}
                    <div className="flex items-center lg:hidden">
                        <button
                            onClick={() => setMobileOpen(!mobileOpen)}
                            className="p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition"
                        >
                            {mobileOpen ? (
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            ) : (
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile menu dropdown */}
            {mobileOpen && (
                <div className="lg:hidden border-t border-slate-200 bg-white">
                    <div className="px-4 py-4 space-y-3">
                        {user ? (
                            <>
                                <div className="text-sm text-slate-600 pb-2 border-b border-slate-100">
                                    {user.email}
                                    {isAdmin && <span className="block text-xs text-primary-600 font-medium">ADMINISTRATOR</span>}
                                </div>

                                {isAdmin && (
                                    <Link to="/admin-verification" className="block px-4 py-3 bg-primary-800 text-white rounded-lg text-sm font-medium text-center">
                                        Admin Panel
                                    </Link>
                                )}

                                {!isAdmin && (
                                    <>
                                        {!hasProfile && userType && (
                                            <Link to={userType === 'expert' ? '/expert-intake' : '/attorney-intake'} className="block px-4 py-3 bg-secondary-600 text-white rounded-lg text-sm font-medium text-center">
                                                Complete Registration
                                            </Link>
                                        )}
                                        {hasProfile && !profileVerified && (
                                            <div className="px-4 py-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg text-sm font-medium text-center">
                                                Pending Verification
                                            </div>
                                        )}
                                        {hasProfile && (
                                            <Link to={userType === 'attorney' ? '/attorney-dashboard' : '/expert-dashboard'} className="block px-4 py-3 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50">
                                                Dashboard
                                            </Link>
                                        )}
                                    </>
                                )}

                                {hasProfile && (
                                    <Link to="/my-profile" className="block px-4 py-3 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50">
                                        Profile
                                    </Link>
                                )}

                                <button onClick={handleSignOut} className="w-full px-4 py-3 rounded-lg text-sm font-medium text-red-700 hover:bg-red-50 text-left">
                                    Sign Out
                                </button>
                            </>
                        ) : (
                            <>
                                <Link to="/how-it-works" className="block px-4 py-3 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50">How It Works</Link>
                                <Link to="/pricing" className="block px-4 py-3 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50">Pricing</Link>
                                <a href="mailto:support@expertlinkpro.com" className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50">Support</a>
                                <Link to="/login" className="block px-4 py-3 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50">Sign In</Link>
                                <Link to="/signup" className="block px-4 py-3 bg-primary-800 text-white rounded-lg text-sm font-medium text-center">Get Started</Link>
                            </>
                        )}
                    </div>
                </div>
            )}
        </nav>
    );
}
